import React, { useRef } from 'react';
import classnames from 'classnames';
import { isFunction } from 'lodash-es';
import SvgIcon from '@home/components/SvgIcon';
import FullScreenHeader, { IFullScreenHeaderProps } from './components/FullScreenHeader';
import useFullScreen from '@home/components/FullScreenScale/_hooks/useFullScreen';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

// 全屏、缩放容器组件props
interface IFullScreenScaleProps extends IBaseProps {
  // 全屏时，左上角的title
  title?: IFullScreenHeaderProps['title'];
  // 是否禁用缩小按钮
  smallerBtnDisabled?: boolean;
  // 是否禁用放大按钮
  biggerBtnDisabled?: boolean;
  // 点击缩小
  onSmaller?: () => void;
  // 点击放大
  onBigger?: () => void;
  // 切换是否全屏的回调
  onFullScreenChange?: (isFullScreen: boolean) => void;
  // 全屏展示内容dom、ReactElement
  children: (payload: {
    isFullScreen: boolean;
    fullScreenContent: React.RefObject<HTMLDivElement | null>;
  }) => React.ReactElement | null;
}

const FullScreenScale: React.FC<IFullScreenScaleProps> = (props) => {
  const {
    style,
    className,
    title,
    smallerBtnDisabled = false,
    biggerBtnDisabled = false,
    onSmaller,
    onBigger,
    onFullScreenChange,
    children,
  } = props;

  // 需要全屏展示的dom
  const fullScreenContent = useRef<HTMLDivElement>(null);

  const { isFullScreen, requestFullScreen, exitFullScreen } = useFullScreen(fullScreenContent, onFullScreenChange);

  return (
    <div style={style} className={classnames('dbh-fullscreen-scale', className)}>
      {/* 全屏按钮  */}
      <div className="dbh-fullscreen-btn" onClick={requestFullScreen}>
        <SvgIcon className="dbh-fullscreen-btn-focus" type="focus-icon" />
      </div>
      {/* 全屏展示内容容器 */}
      <div className={classnames('dbh-fullscreen', { 'dbh-fullscreen-context': isFullScreen })} ref={fullScreenContent}>
        {/* 放大按钮 */}
        {isFullScreen && (
          <div
            className={classnames('dbh-fullscreen-btn dbh-fullscreen-btn--enlarge', { disabled: biggerBtnDisabled })}
            onClick={(e) => {
              e.stopPropagation();
              onBigger?.();
            }}
          >
            <SvgIcon className="dbh-fullscreen-btn-cross" type="cross" />
          </div>
        )}
        {/* 缩小按钮 */}
        {isFullScreen && (
          <div
            className={classnames('dbh-fullscreen-btn dbh-fullscreen-btn--narrow', { disabled: smallerBtnDisabled })}
            onClick={(e) => {
              e.stopPropagation();
              onSmaller?.();
            }}
          >
            <SvgIcon className="dbh-fullscreen-btn-narrow" type="jian" />
          </div>
        )}
        {/* 全屏header  */}
        {isFullScreen && <FullScreenHeader title={title} exitFullScreen={exitFullScreen} />}
        {/* 全屏展示内容 */}
        {isFunction(children) && children({ isFullScreen, fullScreenContent })}
      </div>
    </div>
  );
};

export default FullScreenScale;
